import { EventEmitter, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { GuidEmpty, TableHeader } from 'mbs-ui-kit';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import { StorageType } from 'mbs-ui-shared@models';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { storageAccountsToDestinationsArray } from 'mbs-ui-shared@utils/storage-accounts-to-destinations-array';
import { catchError, combineLatest, filter, finalize, first, map, Observable, of, timeout } from 'rxjs';
import { AgentVersionForNBFHybrid, AgentVersionForNBFHybridIgnore } from 'wizards/helpers/bases/base-for-steps-helper';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';

const VersionForHybrid = 711;
const VersionForEU = 860;
const VersionForEUUnix = 450;

export interface WhereBackupStepLogicHook {
  isNetBuild: boolean;
  isNBF: boolean;
  isEdit: boolean;
  isUnix: boolean;
  isRestore: boolean;
  isRDMode: boolean;
  isSQLPlan: boolean;
  planType: PlanTypes;
  isConsistencyCheck: boolean;
  changeStorage: EventEmitter<unknown>;
  forceValid: Function;
  detectChanges: Function;
}

export class WhereBackupStepLogic {
  public validVersionForEU = false;
  public validVersionForNBFWithHybrid = false;
  public correctlyVersionForShowHybrid = false;

  public firstLoad = true;

  public CloudStorages: StorageConnection[] = [];
  public LocalStorages: StorageConnection[] = [];
  public MixStorages: StorageConnection[] = [];
  public selectedNotLocalStorage: StorageConnection = null;
  public selectedLocalStorage: StorageConnection = null;
  public selectedLocalStorages: string[] = [];
  public selectedMixStorages: string[] = [];

  public disabledRowText = this.i18nPipe.transform('wizards:whereBackup.whereBackupConsistencyDisabled');
  public disabledRowOtherUnixText = this.i18nPipe.transform('wizards:whereBackup.whereBackupEUUnixDisabled');
  public disabledRowOtherText = this.i18nPipe.transform('wizards:whereBackup.whereBackupEUDisabled');
  public notLocalStorageTexts = this.i18nPipe.transform('wizards:not_local_storage_text', { returnObjects: true });

  private consistencyCheckMap: { [key: string]: boolean } = this.hooks.isConsistencyCheck
    ? cloneDeep(this.backupFacade.consistencyCheckMap)
    : {};
  private storages: Storages;
  private stepForm: FormGroup<{ IsHybridBackup: FormControl<boolean>; ConnectionID: FormControl<string>; HybridID: FormControl<string> }>;

  public static headers: TableHeader[] = [
    {
      name: '',
      gridColSize: '10fr',
      class: '-stretch'
    }
  ];

  get noSelectedStorages(): boolean {
    return this.noSelectedPrimary && this.noSelectedHybrid;
  }

  get noSelectedPrimary(): boolean {
    const connectionId = this.stepForm.controls.ConnectionID.value;

    return !connectionId || connectionId === GuidEmpty;
  }

  get noSelectedHybrid(): boolean {
    const HybridID = this.stepForm.controls.HybridID.value;

    return !HybridID || HybridID === GuidEmpty;
  }

  get existAllStorages(): boolean {
    return this.storages && (this.hooks.isSQLPlan || !!this.storages.LocalStorages.length) && !!this.storages.CloudStorages.length;
  }

  get nbfWithHybrid(): boolean {
    return this.hooks.isNBF && this.validVersionForNBFWithHybrid;
  }

  get canBeHybrid(): boolean {
    return this.hooks.planType !== PlanTypes.CloudVMBackupEC2Plan && !this.hooks.isRestore;
  }

  public applyingCreatedDestination = false;

  get loadingTableDataForLocalOrCloudTable(): boolean {
    return !this.storages || this.applyingCreatedDestination;
  }

  get needHideHybridControls(): boolean {
    return this.hooks.isEdit && (this.hooks.planType === PlanTypes.BackupDiskImagePlan || this.correctlyVersionForShowHybrid);
  }

  get notExistRepositoryForCorrectStepState(): boolean {
    return this.stepForm.get('IsHybridBackup').value ? !this.CloudStorages.length : !this.MixStorages.length;
  }

  get needShowRadioButtons(): boolean {
    return this.needShowRadioButtonsNewDestination && !this.needHideHybridControls;
  }

  get needShowRadioButtonsNewDestination(): boolean {
    return this.supportedModsAndTypes && !this.notExistRepositoryForCorrectStepState && this.existAllStorages;
  }

  get nbfAndCantHybridVersion(): boolean {
    return !this.hooks.isRDMode && (!this.hooks.isUnix || this.hooks.isNetBuild) && this.hooks.isNBF && !this.validVersionForNBFWithHybrid;
  }

  get supportedModsAndTypes(): boolean {
    return (
      !this.hooks.isRDMode &&
      (!this.hooks.isNBF || this.validVersionForNBFWithHybrid) &&
      !this.hooks.isRestore &&
      (!this.hooks.isUnix || this.hooks.isNetBuild) &&
      !this.hooks.isConsistencyCheck
    );
  }

  constructor(
    private mainService: RemoteManagementWizardsService,
    private backupFacade: ComputerBackupFacade,
    private i18nPipe: I18NextPipe,
    private hooks: WhereBackupStepLogicHook
  ) {
    const version = this.mainService.backupVersionUpdated;
    const versionNum = version ? +version.substring(0, 3) : 0;

    this.correctlyVersionForShowHybrid = versionNum > VersionForHybrid;
    this.validVersionForNBFWithHybrid = versionNum >= AgentVersionForNBFHybrid && !AgentVersionForNBFHybridIgnore.includes(versionNum);
    this.validVersionForEU = versionNum >= (this.hooks.isUnix && !this.hooks.isNetBuild ? VersionForEUUnix : VersionForEU);
  }

  init() {
    if (this.hooks.isEdit) {
      this.disabledRowText = this.i18nPipe.transform('wizards:whereBackup.whereBackupConsistencyDisabledEdit');
    }
  }

  getForm(): typeof this.stepForm {
    this.stepForm = new FormGroup({
      IsHybridBackup: new FormControl({ value: false, disabled: this.hooks.isRDMode || !this.canBeHybrid }),
      ConnectionID: new FormControl(''),
      HybridID: new FormControl('')
    });

    return this.stepForm;
  }

  switchToLocalOrCloudOption(event): void {
    event.preventDefault();

    this.stepForm.get('IsHybridBackup').setValue(false);
  }

  isHybridBackupChangeHandler(event): void {
    const formValue = this.stepForm.value;
    const ConnectionID = this.hooks.isNBF && formValue.IsHybridBackup ? formValue.HybridID : formValue.ConnectionID;

    if (event) {
      if (~this.LocalStorages.findIndex((s) => s.ID === ConnectionID)) {
        this.stepForm.get('HybridID').setValue(ConnectionID);
        this.stepForm.get('ConnectionID').setValue(GuidEmpty);
        this.disableFieldsInLists(ConnectionID, false);
      }
    } else {
      const value = ConnectionID && ConnectionID !== GuidEmpty ? ConnectionID : formValue.HybridID;
      this.stepForm.get('HybridID').setValue(GuidEmpty);
      this.stepForm.get('ConnectionID').setValue(value);
      this.updateStorageArray('MixStorages', value);
      this.disableFieldsInLists(null, false);
    }

    this.disableEUIfNeed();
  }

  changeSelectedHandler(selectedStorage: string[], firstTable = true): void {
    const value = this.stepForm.value;
    const selectedStorageId = selectedStorage[0];

    if (selectedStorage.length) {
      if (firstTable) {
        this.stepForm.get('ConnectionID').setValue(selectedStorageId);
        this.selectedNotLocalStorage = this.MixStorages.find((s) => s.ID === selectedStorageId);

        this.forceEmitChangeStorage(selectedStorageId);
      } else if (value?.IsHybridBackup) {
        this.stepForm.get('HybridID').setValue(selectedStorageId);
        this.selectedLocalStorage = this.MixStorages.find((s) => s.ID === selectedStorageId);

        if (this.hooks.isNBF || this.hooks.isEdit) this.forceEmitChangeStorage(this.stepForm.get('ConnectionID').value);
      }
    } else {
      if (firstTable) {
        this.stepForm.get('ConnectionID').setValue(GuidEmpty);
        delete this.selectedNotLocalStorage;

        setTimeout(() => this.hooks.changeStorage.emit(null));
      } else if (value?.IsHybridBackup) {
        this.stepForm.get('HybridID').setValue(GuidEmpty);
        delete this.selectedLocalStorage;

        if (this.hooks.isNBF) this.forceEmitChangeStorage(this.stepForm.get('ConnectionID').value);
      }
    }

    this.disableEUIfNeed();
  }

  updateSelectedItems(HybridID: string, ConnectionID: string): void {
    if (this.stepForm.get('IsHybridBackup').value) {
      return this.hybridUpdate(HybridID, ConnectionID);
    }

    this.notHybridUpdate(HybridID, ConnectionID);
  }

  selectFirstItems(fieldName: string, storageArrayName: string): void {
    this.stepForm.get(fieldName).patchValue(this[storageArrayName][0].ID);

    setTimeout(() => {
      this.hooks.changeStorage.emit(this[storageArrayName][0]);
      this.hooks.forceValid();
    });
  }

  // The step's own list (plan.Storages) is a plan-type-filtered subset, so a before/after diff must use
  // the raw destinations of the computer instead — otherwise any pre-existing filtered-out item looks "new"
  snapshotDestinationIds(injector: Injector): Observable<Set<string>> {
    const hid = this.mainService.hid;

    this.backupFacade.loadDestinations({ hid });

    const destinations$ = toObservable(this.backupFacade.destinationsByHid(hid), { injector });
    const loaded$ = toObservable(this.backupFacade.destinationsByHidLoaded(hid), { injector });

    return combineLatest([destinations$, loaded$]).pipe(
      filter(([, loaded]) => loaded),
      first(),
      map(([destinations]) => new Set(storageAccountsToDestinationsArray(destinations || []).map(({ ID }) => ID)))
    );
  }

  // Reloads the computer's destinations after an in-place creation and surfaces the new one on top of the lists
  applyCreatedDestination(injector: Injector, knownIds: Set<string>): Observable<string> {
    const hid = this.mainService.hid;

    this.applyingCreatedDestination = true;

    this.backupFacade.loadDestinations({ hid, force: true });

    const destinations$ = toObservable(this.backupFacade.destinationsByHid(hid), { injector });
    const loading$ = toObservable(this.backupFacade.destinationsByHidLoading(hid), { injector });

    return combineLatest([destinations$, loading$]).pipe(
      filter(([, loading]) => !loading),
      map(([destinations]) => {
        const storages = { CloudStorages: [], LocalStorages: [] } as Storages;
        storageAccountsToDestinationsArray(destinations || []).forEach((destination) =>
          storages[destination.StorageType === StorageType.LocalFS ? 'LocalStorages' : 'CloudStorages'].push(destination)
        );
        this.setStorages(storages);

        const created = this.MixStorages.find(({ ID }) => !knownIds.has(ID));

        if (!created) return null;

        this.updateStorageArray('CloudStorages', created.ID);
        this.updateStorageArray('MixStorages', created.ID);

        return created.ID;
      }),
      // A stale store emission can arrive before the forced reload lands — wait for the one that carries the new item
      first((createdId) => !!createdId),
      finalize(() => (this.applyingCreatedDestination = false))
    );
  }

  // The templates list is keyed by the Apply-to scope, not a computer — it reloads through the shared facade,
  // and the created destination is recognized by the account id the storage wizard generated
  applyCreatedDestinationForRD(rdFacade: RemoteDeployDataFacade, createdAccountId: string): Observable<string> {
    // Backend guids come back in varying case and sometimes braced, unlike the client-generated one
    const normalizeId = (value: string): string => value?.replace(/[{}]/g, '').toLowerCase();
    const knownIds = new Set(this.MixStorages.map(({ ID }) => ID));

    this.applyingCreatedDestination = true;
    rdFacade.getDestinations(null, this.mainService.rdConfigurationId);

    return rdFacade.SAWithDestinations$.pipe(
      // Without the cap a failed reload or an unmatched id would leave the spinner on forever
      timeout(30_000),
      map((accounts) => {
        const createdAccount = accounts.find(({ id }) => normalizeId(id) === normalizeId(createdAccountId));
        // If the reloaded list carries the account under an unrecognizable id, the bucket missing from the snapshot is the created one
        const buckets = createdAccount ? createdAccount.buckets : accounts.flatMap(({ buckets: accountBuckets }) => accountBuckets);
        const created = buckets.find(({ id }) => !knownIds.has(id));

        if (!created) return null;

        // The steps take the Storages input only while their lists are empty, so the reloaded list is pushed directly
        const storages = SupportMethodsForStepsFromPlan.getStoragesFromStorageAccounts(accounts);

        if (this.hooks.isRDMode && this.hooks.isUnix) {
          storages.LocalStorages = storages.LocalStorages.filter(({ StorageType: type }) => type !== StorageType.LocalFS);
        }

        this.setStorages(storages);
        this.updateStorageArray('CloudStorages', created.id);
        this.updateStorageArray('MixStorages', created.id);

        return created.id;
      }),
      // The BehaviorSubject replays the pre-reload list first — wait for the emission that carries the new destination
      first((createdId) => !!createdId),
      catchError(() => of<string>(null)),
      finalize(() => (this.applyingCreatedDestination = false))
    );
  }

  updateStorageArray(arrayName: string, selectedId: string): void {
    const idx = this[arrayName].findIndex((i) => i.ID === selectedId);

    if (~idx) {
      this[arrayName].unshift(...this[arrayName].splice(idx, 1));
      this[arrayName] = Array.from(this[arrayName]);
      this.firstLoad = false;
    }
  }

  idIsNotEmpty(id): boolean {
    return !!id && id !== GuidEmpty;
  }

  setStorages(storages: Storages): void {
    this.storages = { ...storages, CloudStorages: storages.CloudStorages.filter((s) => s.StorageType !== StorageType.AzureVM) };
    this.CloudStorages = this.storages.CloudStorages;
    this.LocalStorages = this.storages.LocalStorages;
    this.MixStorages = cloneDeep(this.CloudStorages).concat(cloneDeep(this.LocalStorages));
    if (this.hooks.isConsistencyCheck) {
      this.MixStorages.forEach(
        (s) => (s.disabled = this.hooks.isConsistencyCheck && (this.hooks.isEdit || this.consistencyCheckMap[s.ID]))
      );
    }

    this.disableEUIfNeed();
  }

  disableEUIfNeed(): void {
    if (!this.validVersionForEU) {
      const disabledRowTooltipText =
        this.hooks.isUnix && !this.hooks.isNetBuild ? this.disabledRowOtherUnixText : this.disabledRowOtherText;

      this.MixStorages.forEach((s) => {
        const isEU = s.StorageType === StorageType.AmazonS3Eu;

        s.disabledRowTooltipText = isEU ? disabledRowTooltipText : null;

        s.disabled = s.disabled || isEU;
      });
    }
  }

  disableFieldsInLists(id: string, firstTable: boolean): void {
    this[firstTable ? 'LocalStorages' : 'MixStorages'].forEach((storage: StorageConnection) => (storage.disabled = storage.ID === id));

    this.hooks.detectChanges();
  }

  private isValidId(id: string, isConnection = true, isHybrid = false): boolean {
    if (!this.idIsNotEmpty(id)) return true;

    if (isHybrid) return isConnection ? this.MixStorages.some((i) => i.ID === id) : this.LocalStorages.some((i) => i.ID === id);

    return this.MixStorages.some((i) => i.ID === id);
  }

  private hybridUpdate(HybridID: string, ConnectionID: string): void {
    if (!this.idIsNotEmpty(ConnectionID) && this.CloudStorages.length === 1) {
      return this.selectFirstItems('ConnectionID', 'CloudStorages');
    }

    if (!this.idIsNotEmpty(HybridID) && this.LocalStorages.length === 1) {
      return this.selectFirstItems('HybridID', 'LocalStorages');
    }

    if (this.isValidId(ConnectionID, true, true) && this.isValidId(HybridID, false, true))
      this.updateSelectedIfHybrid(HybridID, ConnectionID);
    else this.resetFormItemsAndValidate();

    this.hooks.detectChanges();
  }

  private notHybridUpdate(HybridID: string, ConnectionID: string): void {
    const id = ConnectionID && ConnectionID !== GuidEmpty ? ConnectionID : HybridID;

    if (!this.idIsNotEmpty(id) && this.MixStorages.length === 1 && !this.MixStorages[0].disabled) {
      return this.selectFirstItems('ConnectionID', 'MixStorages');
    }

    if (this.isValidId(id)) this.updateSelectedIfNotHybrid(id);
    else this.resetFormItemsAndValidate();

    this.hooks.detectChanges();
  }

  private updateSelectedIfHybrid(HybridID: string, ConnectionID: string): void {
    if (this.isValidId(ConnectionID, true, true) && this.isValidId(HybridID, false, true)) {
      if (HybridID) this.selectedLocalStorages = [HybridID];
      if (ConnectionID) this.selectedMixStorages = [ConnectionID];
      if (this.hooks.isEdit && this.firstLoad) {
        this.updateStorageArray('LocalStorages', this.selectedLocalStorages[0]);
        this.updateStorageArray('CloudStorages', this.selectedMixStorages[0]);
        if (!this.firstLoad) this.stepForm.updateValueAndValidity();
      }
    } else this.resetFormItemsAndValidate();
  }

  private updateSelectedIfNotHybrid(id): void {
    if (this.isValidId(id)) {
      this.selectedMixStorages = [id];

      if (this.hooks.isEdit && this.firstLoad) {
        this.updateStorageArray('MixStorages', id);

        if (this.hooks.isRDMode) {
          const selected = this.MixStorages.find((i) => i.ID === id);

          selected && this.hooks.changeStorage.emit(selected);
        }
        if (!this.firstLoad) this.stepForm.updateValueAndValidity();
      }
    } else this.resetFormItemsAndValidate();
  }

  private resetFormItemsAndValidate(): void {
    const data = this.stepForm.value;

    this.firstLoad = false;
    data.ConnectionID = GuidEmpty;
    data.HybridID = GuidEmpty;

    if (this.stepForm && !this.existAllStorages) data.IsHybridBackup = false;

    this.stepForm.reset(data);
    setTimeout(() => this.hooks.forceValid());
  }

  private forceEmitChangeStorage(id: string): void {
    const selected = this.MixStorages.find((i) => i.ID === id);

    selected && setTimeout(() => this.hooks.changeStorage.emit(selected));
  }
}
